import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialog } from '../../../hooks/use-dialog';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { GcpFileImporter } from '../gcp-file-importer/gcp-file-importer';
import { GcpRow } from '../gcp-parser';

type Props = {
  dialog: ReturnType<typeof useDialog>;
  onImport: (data: { filename: string; gcpPoints: GcpRow[] }) => Promise<void>;
};

export const UploadGcpDialog = ({ dialog, onImport }: Props) => {
  const { t } = useTranslation();
  const [importedData, setImportedData] = useState<{
    filename: string;
    gcpPoints: GcpRow[];
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleImportFromImporter = (data: { filename: string; gcpPoints: GcpRow[] }) => {
    setImportedData(data);
    setIsConfirming(true);
  };

  const handleConfirm = async () => {
    if (!importedData) {
      return;
    }

    await onImport(importedData);
    dialog.hide();
    // Reset state when dialog closes
    setImportedData(null);
    setIsConfirming(false);
  };

  const handleDataChange = (data: { filename: string; gcpPoints: GcpRow[] }) => {
    setImportedData(data);
  };

  return (
    <Dialog closeIcon={true} height={'expand'} maxWidth={800} width={800} onClose={dialog.hide}>
      {{
        header: t('gcpList.uploadGcpFile', { ns: 'cloudProcessing' }),
        content: (
          <GcpFileImporter onDataChange={handleDataChange} onImport={handleImportFromImporter} />
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={!isConfirming}
                variant="contained"
                onClick={handleConfirm}
              >
                {t('confirm', { ns: 'common' })}
              </Button>
              <Button variant="text" onClick={dialog.hide}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
